import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "./components/Navbar";
import SignupPopup from "./components/SignupPopup";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import GoogleAnalytics from "./components/GoogleAnalytics";
import StructuredData from "./components/StructuredData";
import SEO from "./components/SEO";
import VisitorPresence from "./components/VisitorPresence";
import { useScrollReveal } from "./hooks/useScrollReveal";
import { loadCompanyInfo } from "./constants/companyInfo";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CustomProductDetailPage from "./pages/CustomProductDetailPage";
import BlogsPage from "./pages/BlogsPage";
import PurchasingGuidePage from "./pages/PurchasingGuidePage";
import BlogDetailPage from "./pages/BlogDetailPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import AdminDashboard from "./admin/AdminDashboard";
import AdminLogin from "./admin/AdminLogin";
import CartPage from "./pages/CartPage";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import AuthPage from "./pages/AuthPage";
import ResetPasswordConfirmPage from "./pages/ResetPasswordConfirmPage";
import AccountPage from "./pages/AccountPage";
import ProtectedRoute from "./components/ProtectedRoute";
import CheckoutPage from "./pages/CheckoutPage";
import FAQPage from "./pages/FAQPage";
import ArtworkGuidelinesPage from "./pages/ArtworkGuidelinesPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SubmitOrderPage from "./pages/SubmitOrderPage";
import InvoicePaymentResultPage from "./pages/InvoicePaymentResultPage";
import "./styles/global.css";
import "./styles/animations.css";

function PublicLayout({ children }) {
  const location = useLocation();
  useScrollReveal([location.pathname]);
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <SignupPopup />
    </>
  );
}

export default function App() {
  // Preload company info from admin panel at app startup (for caching)
  useEffect(() => {
    loadCompanyInfo().catch(err => {
      console.warn("[App] Failed to preload company info, defaults will be used:", err);
    });
  }, []);

  return (
    <AuthProvider>
    <CartProvider>
    <BrowserRouter>
      <ScrollToTop />
      <GoogleAnalytics />
      <SEO />
      <StructuredData />
      <VisitorPresence />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/products" element={<PublicLayout><ProductsPage /></PublicLayout>} />
        {/* Custom product detail */}
        <Route path="/products/custom/:id" element={<PublicLayout><CustomProductDetailPage /></PublicLayout>} />
        {/* Sinalite product detail */}
        <Route path="/products/:id" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
        <Route path="/blogs" element={<PublicLayout><BlogsPage /></PublicLayout>} />
        <Route path="/purchasing-guide" element={<PublicLayout><PurchasingGuidePage /></PublicLayout>} />
        <Route path="/blogs/:slug" element={<PublicLayout><BlogDetailPage /></PublicLayout>} />
        <Route path="/cart" element={<PublicLayout><CartPage /></PublicLayout>} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordConfirmPage />} />
        <Route path="/account" element={<ProtectedRoute><PublicLayout><AccountPage /></PublicLayout></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><PublicLayout><CheckoutPage /></PublicLayout></ProtectedRoute>} />
        <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
        <Route path="/artwork-guidelines" element={<PublicLayout><ArtworkGuidelinesPage /></PublicLayout>} />
        <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
        <Route path="/submit-order" element={<PublicLayout><SubmitOrderPage /></PublicLayout>} />
        <Route path="/invoice-payment-success" element={<InvoicePaymentResultPage />} />
        <Route path="/invoice-payment-cancelled" element={<InvoicePaymentResultPage />} />

        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/*" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
    </CartProvider>
    </AuthProvider>
  );
}
